import { Controller } from "stimulus"
import { getCookieWithConsent, setCookieWithConsent } from "../utils/cookies"
import { Consent } from "../utils/consent"
import { getLocalStorageWithConsent, setLocalStorageWithConsent } from "../utils/storage"
import ga from "../utils/ga"

export default class extends Controller {
  static values = {
    url: String,
    data: Object,
    category: String,
  }

  static targets = ["css", "html"]

  static classes = ["dismiss"]

  connect() {
    this.pickAndDisplayBanner(this.dataValue.banners)
    this.reportHeaderBannerImpression()
  }

  click(event) {
    const clickedDismissOrChild = Boolean(event.target.closest(".js-banner__dismiss"))
    if (clickedDismissOrChild) {
      event.preventDefault()
      this.onDismiss()
    } else {
      this.reportHeaderBannerClick()
    }
  }

  onDismiss() {
    this.hideBanner()
    if (this.banner) {
      setLocalStorageWithConsent(this.getBannerCacheId(this.banner), "hidden", Consent.marketing)
    }
    this.reportHeaderBannerClose()
  }

  pickAndDisplayBanner(banners) {
    this.banner = this.pickBanner(banners)
    if (this.banner) {
      if (this.hasHtmlTarget) {
        this.htmlTarget.innerHTML = this.banner.html
      }
      this.cssTarget.innerHTML = this.banner.css
      this.showBanner()
    } else {
      this.hideBanner()
    }
  }

  hideBanner() {
    this.element.classList.remove(this.element.dataset.bannerShowClass)
  }

  showBanner() {
    this.element.classList.add(this.element.dataset.bannerShowClass)
  }

  pickBanner(banners) {
    if (banners && banners.length > 0) {
      const pageKey = this.getPageKey()
      const banner = this.getPreviouslyPickedBanner(banners, pageKey) || this.pickRandomBanner(banners, pageKey)
      const selectedBanner = this.isDismissed(banner) ? null : banner

      return selectedBanner
    }
    return null
  }

  getPageKey() {
    return this.categoryValue || "global"
  }

  globalBanners(banners) {
    return banners.filter((banner) => banner.categories === undefined || banner.categories.length === 0)
  }

  getPreviouslyPickedBanner(nonDismissedBanners, pageKey) {
    const previouslyPickedBannerId = this.getPreviouslyPickedBannerIds()[pageKey]
    return nonDismissedBanners.find((banner) => banner.id === previouslyPickedBannerId)
  }

  pickRandomBanner(banners, pageKey) {
    let filteredBanners
    if (pageKey === "global") {
      filteredBanners = this.globalBanners(banners)
    } else {
      filteredBanners = banners.filter((banner) => banner.categories && banner.categories.includes(pageKey))
      if (filteredBanners.length === 0) {
        filteredBanners = this.globalBanners(banners)
      }
    }
    const indexPicked = Math.floor(Math.random() * filteredBanners.length)
    const pickedBanner = filteredBanners[indexPicked]
    if (pickedBanner) {
      this.addCachedBannerId(pageKey, pickedBanner.id)
    }

    return pickedBanner
  }

  getPreviouslyPickedBannerIds() {
    const ids = getCookieWithConsent("marketplace_banners", Consent.marketing, JSON.stringify({}))
    return ids ? JSON.parse(ids) : {}
  }

  addCachedBannerId(pageKey, bannerId) {
    setCookieWithConsent(
      "marketplace_banners",
      JSON.stringify({
        ...this.getPreviouslyPickedBannerIds(),
        [pageKey]: bannerId,
      }),
      Consent.marketing
    )
  }

  getBannerCacheId(banner) {
    if (banner) {
      return `banner_${banner.id}_cache`
    }
    return null
  }

  isDismissed(banner) {
    return getLocalStorageWithConsent(this.getBannerCacheId(banner), Consent.marketing) === "hidden"
  }

  reportHeaderBannerClick() {
    if (this.banner) {
      ga("ec:addPromo", this.bannerPayload())
      ga("ec:setAction", "promo_click")
      ga("send", "event", "Internal Promotions", "click", {
        label: this.banner.id,
      })
    }
  }

  reportHeaderBannerClose() {
    if (this.banner) {
      ga("ec:addPromo", this.bannerPayload())
      ga("ec:setAction", "promo_click")
      ga("send", "event", "Banner Impression", "close", {
        label: this.banner.id,
      })
    }
  }

  reportHeaderBannerImpression() {
    if (this.banner) {
      ga("ec:addPromo", this.bannerPayload())
      ga("send", "event", "Banner Impression", "view", {
        id: this.banner.id,
        label: this.banner.id,
      })
    }
  }

  bannerPayload() {
    return { id: this.banner.id, name: this.banner.id }
  }
}
